import { ConnectedApps } from "@WahooFitness/cloud-client-ts";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ConnectedAppType, ConnectedAppUserType } from "@WahooFitness/cloud-client-types";
import { DataConnected } from "@carbon/icons-react";
import {
  useSnackbarContext,
  useConfigContext,
  useCloudContext,
  useNativeMessagingContext,
  useOfflineSWR,
} from "@WahooFitness/wahoo-offline-mfe";
import { t } from "@lingui/macro";
import { MenuListItemType, MenuListItemVariant } from "@WahooFitness/redesignr";
import useNativeHealth from "./useNativeHealth";
import useAppleHealthWorkoutImportEnabled from "./useAppleHealthWorkoutImportEnabled";
import useRecoverySourceAutoSelect, {
  AuthorizedRecoveryCandidate,
} from "./useRecoverySourceAutoSelect";
import { clearPendingRecoverySourceAutoSelect } from "./recoverySourceAutoSelectPending";
import { Box } from "@mui/material";
import { useSettingsRemoteConfig } from "@/hooks/useSettingsRemoteConfig";
import { useSettingsVersionedFeatures } from "@/hooks/useSettingsVersionedFeatures";

export interface ConnectedAppListItem extends MenuListItemType {
  authorized: Partial<ConnectedAppUserType> | null;
  supportedCapabilities: Array<keyof ConnectedAppUserType>;
  fitnessAppId?: number;
  // Resolves true once the app is actually deauthorized, so the caller can release anything
  // still pointing at it (the recovery source slot) without doing so after a failed request.
  handleDeauth: () => Promise<boolean>;
  nativeHealth?: boolean;
  /// Apple Health sharing is on, but some write permissions are missing, so workouts export with that
  /// data silently dropped. Rendered as a warning plus a fix action inside AuthorizedApp — the row's
  /// own `action` is never reached once the app is authorized, because the list renders the accordion
  /// instead. See DEX-1189.
  nativeHealthIncomplete?: boolean;
  /// The missing permissions are already answered, so the fix action opens the Health app rather than
  /// re-requesting. Only affects the label — native owns the routing either way.
  nativeHealthIncompleteHealthAppOnly?: boolean;
  /// Stable, non-display keys (e.g. "heartRate", "activeEnergy") naming which write types are behind
  /// `nativeHealthIncomplete`, so AuthorizedApp can name them instead of a generic message.
  nativeHealthMissingWriteTypeKeys?: string[];
  fixNativeHealth?: () => void;
  /// Native read an HRV sample, so the "no data yet" hint is no longer needed.
  nativeHealthRecoveryDataReadable?: boolean;
}

const EMAIL_APP_ID = 13;

export const availableCapabilities = [
  "share_summaries",
  "import_summaries",
  "import_plans",
  "import_routes",
  "import_segments",
  "share_workouts",
  "import_vitals",
  "import_workouts",
];

const useThirdPartySharing = (filterByCapability?: keyof ConnectedAppUserType) => {
  const {
    wahooToken,
    cloudConfig: { wahooLinksUrl },
  } = useConfigContext();
  const { getCloudClient } = useCloudContext();
  const { addRefreshListener, removeRefreshListener } = useNativeMessagingContext();
  const { getSettingsRemoteConfigObject } = useSettingsRemoteConfig();
  const supportedSharingApps = useMemo(() => {
    return getSettingsRemoteConfigObject<number[]>("SUPPORTED_SHARING_APPS") ?? [];
  }, [getSettingsRemoteConfigObject]);

  const { enabled: recovery247Enabled } = useSettingsVersionedFeatures(
    "RECOVERY_247_MINPROFILE_VERSION"
  );
  const { enabled: appleHealthRecoverySourceEnabled } = useSettingsVersionedFeatures(
    "RECOVERY_247_APPLE_HEALTH_MINPROFILE_VERSION"
  );
  const { appleHealthWorkoutImportEnabled } = useAppleHealthWorkoutImportEnabled();
  // import_vitals is the 24/7 recovery capability — only recognized once the feature is
  // enabled for this account, independent of any app's own backend capability state.
  // import_workouts likewise follows Apple Health's workout import gate.
  const recognizedCapabilities = useMemo(
    () =>
      availableCapabilities.filter(
        (capability) =>
          (capability !== "import_vitals" || recovery247Enabled) &&
          (capability !== "import_workouts" || appleHealthWorkoutImportEnabled)
      ),
    [recovery247Enabled, appleHealthWorkoutImportEnabled]
  );

  const connectedAppsClient = useMemo(() => getCloudClient(ConnectedApps), [getCloudClient]);

  const {
    data: connectedAppsData,
    error: connectedAppsError,
    isLoading: connectedAppsLoading,
    mutate: mutateConnectedApps,
  } = useOfflineSWR(["getConnectedApps", wahooToken], ([_key, token]) =>
    connectedAppsClient.get(token)
  );

  useEffect(() => {
    const refreshListener = addRefreshListener(mutateConnectedApps);
    return () => removeRefreshListener(refreshListener);
  }, [addRefreshListener, mutateConnectedApps, removeRefreshListener]);

  const { enqueueSnackbar } = useSnackbarContext();
  const { addNativeHealth, nativeHealthFitnessAppId } = useNativeHealth();
  const { markPendingAutoSelect, settlePendingAutoSelect } = useRecoverySourceAutoSelect();

  const [emailAppData, emailAppIndex] = useMemo(() => {
    const index =
      connectedAppsData?.findIndex((connectedApp) => connectedApp.id === EMAIL_APP_ID) || -1;
    return [connectedAppsData?.[index], index];
  }, [connectedAppsData]);

  const emailAuthorized = useMemo(
    () => !!emailAppData?.connected_app_user,
    [emailAppData?.connected_app_user]
  );

  const replaceEmailConnectedAppUser = useCallback(
    (
      connectedApps: ConnectedAppType[] | undefined,
      connectedAppUser: ConnectedAppUserType | null
    ) => {
      const copy = connectedApps && [...connectedApps];
      if (copy) {
        copy[emailAppIndex] = { ...copy[emailAppIndex], connected_app_user: connectedAppUser };
      }
      return copy;
    },
    [emailAppIndex]
  );

  const handleDeauth = useCallback(
    async (appId: number, appName: string) => {
      let deauthorized = true;
      void clearPendingRecoverySourceAutoSelect();
      try {
        await connectedAppsClient.deauthorizeForUser(appId, wahooToken);
      } catch {
        deauthorized = false;
        enqueueSnackbar({
          message: t`Failed to deauthorize ${appName}. Please check your internet connection and try again.`,
          severity: "error",
        });
      }
      mutateConnectedApps((current) => {
        if (appId !== EMAIL_APP_ID) {
          return undefined;
        }
        return replaceEmailConnectedAppUser(current, null);
      });
      return deauthorized;
    },
    [
      connectedAppsClient,
      enqueueSnackbar,
      mutateConnectedApps,
      replaceEmailConnectedAppUser,
      wahooToken,
    ]
  );

  const updateAutoShareSummaries = useCallback(
    async (appUserId: number, autoShareSummaries: boolean) => {
      try {
        await connectedAppsClient.putConnectedAppUser(
          appUserId,
          { auto_share_summaries: autoShareSummaries },
          wahooToken
        );

        mutateConnectedApps((current) => {
          return current?.map((app) => {
            if (app.connected_app_user?.id === appUserId) {
              return {
                ...app,
                connected_app_user: {
                  ...app.connected_app_user,
                  auto_share_summaries: autoShareSummaries,
                },
              };
            }
            return app;
          });
        });
      } catch {
        enqueueSnackbar({
          message: t`An error occurred when updating auto share summaries. Please try again later.`,
          severity: "error",
        });
      }
    },
    [connectedAppsClient, enqueueSnackbar, mutateConnectedApps, wahooToken]
  );

  const appList = useMemo(() => {
    const mappedApps =
      connectedAppsData
        ?.filter((app) => {
          // Behind RECOVERY_247_MINPROFILE_VERSION: trust the backend's
          // per-account published field instead of the static allow-list.
          const isVisibleForAccount = recovery247Enabled
            ? app.published
            : supportedSharingApps.includes(app.id);
          const hasRequiredCapability = filterByCapability
            ? recognizedCapabilities.includes(filterByCapability) &&
              app[filterByCapability as keyof ConnectedAppType] === true
            : Object.entries(app).some(
                ([key, value]) => recognizedCapabilities.includes(key) && value === true
              );
          return isVisibleForAccount && hasRequiredCapability && app.id !== EMAIL_APP_ID;
        })
        ?.map((app) => {
          const authUrl = new URL(app.connected_app_user ? app.deauthorize_url : app.authorize_url);
          authUrl.searchParams.append("auth_token", wahooToken as string);
          authUrl.searchParams.append(
            "return_to",
            `${wahooLinksUrl}/apps/wahoo_app/settings/third-party-sharing?force=1`
          );
          const supportedCapabilities = (
            recognizedCapabilities as Array<keyof ConnectedAppUserType>
          ).filter((capability) => app[capability as keyof ConnectedAppType]);
          // Runs as the authorize link opens the external browser; the deauthorize link gets none.
          const { fitness_app_id: fitnessAppId } = app;
          const recordAutoSelectIntent =
            !app.connected_app_user &&
            supportedCapabilities.includes("import_vitals") &&
            typeof fitnessAppId === "number"
              ? () => markPendingAutoSelect(fitnessAppId)
              : undefined;
          return {
            authorized: app.connected_app_user,
            supportedCapabilities,
            fitnessAppId,
            id: String(app.id),
            icon: app.icon?.thumb.url ? (
              <Box
                data-testid="sharing-app-icon"
                overflow="hidden"
                borderRadius={2}
                border={1}
                borderColor="divider"
                height={24}
                width={24}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <img width="24" height="24" alt={app.name} src={app.icon.url} />
              </Box>
            ) : (
              <DataConnected width="24" height="24" />
            ),
            content: app.name,
            variant: MenuListItemVariant.ExternalLink,
            linkLocation: authUrl.toString(),
            action: recordAutoSelectIntent,
            handleDeauth: () => handleDeauth(app.id, app.name),
          };
        }) || [];

    // The native health row has no connected_apps record, so the capability filter above never
    // sees it. Apple Health is the one native service that can be a recovery source (it has a
    // cloud fitness_app_id for recovery_fitness_app_id to point at), so it joins the import_vitals
    // flow behind its own flag, and the import_workouts flow behind the workout import gate;
    // Health Connect stays out of any capability-filtered flow.
    const nativeHealthCanBeRecoverySource =
      appleHealthRecoverySourceEnabled && recognizedCapabilities.includes("import_vitals");
    const includeNativeHealth =
      !filterByCapability ||
      (filterByCapability === "import_vitals" &&
        nativeHealthCanBeRecoverySource &&
        nativeHealthFitnessAppId !== undefined) ||
      ((filterByCapability as string) === "import_workouts" &&
        recognizedCapabilities.includes("import_workouts") &&
        nativeHealthFitnessAppId !== undefined);
    return includeNativeHealth
      ? addNativeHealth(mappedApps, {
          canBeRecoverySource: nativeHealthCanBeRecoverySource,
          ...((filterByCapability as string) === "import_workouts" && { listAsAvailable: true }),
        }) || []
      : mappedApps;
  }, [
    addNativeHealth,
    appleHealthRecoverySourceEnabled,
    connectedAppsData,
    filterByCapability,
    markPendingAutoSelect,
    nativeHealthFitnessAppId,
    recognizedCapabilities,
    recovery247Enabled,
    supportedSharingApps,
    wahooToken,
    wahooLinksUrl,
    handleDeauth,
  ]);

  const appOrderMap = useMemo(() => {
    // Behind the flag, SUPPORTED_SHARING_APPS has no role at all — not even
    // ordering — so it isn't a second source of truth alongside `published`.
    if (recovery247Enabled) {
      return new Map<number, number>();
    }
    return new Map(supportedSharingApps.map((id, idx) => [id, idx]));
  }, [recovery247Enabled, supportedSharingApps]);

  const orderFor = useCallback(
    (item: { id: string | number }) => {
      const numericId = Number(item.id);
      // Non-numeric ids (e.g. the native health row) always sort first.
      if (Number.isNaN(numericId)) {
        return -Infinity;
      }
      // Apps not curated in SUPPORTED_SHARING_APPS (or, behind the flag,
      // every app) sort after any that are, preserving their original order.
      return appOrderMap.get(numericId) ?? Infinity;
    },
    [appOrderMap]
  );

  const compareByOrder = useCallback(
    (a: { id: string | number }, b: { id: string | number }) => orderFor(a) - orderFor(b),
    [orderFor]
  );

  const availableApps: MenuListItemType[] = useMemo(
    () => appList.filter((app) => !app.authorized).sort(compareByOrder),
    [appList, compareByOrder]
  );

  // Until Apple Health can be a recovery source, the recovery source list teases it instead.
  const appleHealthComingSoon =
    filterByCapability === "import_vitals" &&
    !appleHealthRecoverySourceEnabled &&
    nativeHealthFitnessAppId !== undefined;

  const authorizedApps: ConnectedAppListItem[] = useMemo(
    () => appList.filter((app) => !!app.authorized).sort(compareByOrder),
    [appList, compareByOrder]
  );

  // Rows that can hold the recovery source slot, by cloud id; the native row qualifies once
  // authorized because it carries Apple Health's cloud fitness_app_id.
  const authorizedRecoveryCandidates = useMemo<AuthorizedRecoveryCandidate[]>(
    () =>
      authorizedApps.flatMap((app) =>
        typeof app.fitnessAppId === "number"
          ? [
              {
                fitnessAppId: app.fitnessAppId,
                name: String(app.content),
                nativeHealth: !!app.nativeHealth,
              },
            ]
          : []
      ),
    [authorizedApps]
  );

  useEffect(() => {
    void settlePendingAutoSelect(authorizedRecoveryCandidates);
  }, [authorizedRecoveryCandidates, settlePendingAutoSelect]);

  const [emailSharingUpdateInProgress, setEmailSharingUpdateInProgress] = useState(false);

  const authorizeEmail = useCallback(async () => {
    setEmailSharingUpdateInProgress(true);
    try {
      const result = await connectedAppsClient.createConnectedAppUser(
        { connected_app_id: EMAIL_APP_ID, auto_share_summaries: true },
        wahooToken
      );
      mutateConnectedApps((current) => {
        return replaceEmailConnectedAppUser(current, result);
      });
    } catch {
      enqueueSnackbar({
        message: t`Failed to authorize ${emailAppData?.name}. Please check your internet connection and try again.`,
        severity: "error",
      });
    }
    setEmailSharingUpdateInProgress(false);
  }, [
    connectedAppsClient,
    emailAppData?.name,
    enqueueSnackbar,
    mutateConnectedApps,
    replaceEmailConnectedAppUser,
    wahooToken,
  ]);

  const deauthorizeEmail = useCallback(async () => {
    setEmailSharingUpdateInProgress(true);
    emailAppData && (await handleDeauth(emailAppData.id, emailAppData.name));
    setEmailSharingUpdateInProgress(false);
  }, [emailAppData, handleDeauth]);

  return {
    connectedAppsData,
    connectedAppsLoading,
    connectedAppsError,
    mutateConnectedApps,
    authorizedApps,
    availableApps,
    appleHealthComingSoon,
    emailAuthorized,
    authorizeEmail,
    deauthorizeEmail,
    emailSharingUpdateInProgress,
    updateAutoShareSummaries,
    // The 24/7 recovery gates, for a caller showing recovery state of its own (TAJ-1641).
    recovery247Enabled,
    appleHealthRecoverySourceEnabled,
  };
};

export default useThirdPartySharing;
